import type { ZoneMessage } from '@/api/types'

/**
 * One thing that was said, however many rows the server wrote to deliver it.
 *
 * `recipients` and `states` are parallel to each other: index i is the state of the delivery to
 * recipient i. A broadcast to twenty members is one entry with twenty of each.
 */
export interface HistoryEntry {
  key: string
  /** Lowest seq in the fan-out — the anchor the jump rail scrolls to. */
  seq: number
  source_type: ZoneMessage['source_type']
  source_name: string
  sender: string
  message: string
  recipients: string[]
  states: ZoneMessage['state'][]
  sent_at: string
  /** The earliest moment any recipient read or peeked it (WI4); null while nobody has. */
  first_read_at: string | null
}

/** The earlier of two instants, treating null as "never". */
function earliest(a: string | null, b: string | null): string | null {
  if (a === null) return b
  if (b === null) return a
  return Date.parse(b) < Date.parse(a) ? b : a
}

/**
 * Collapse per-recipient delivery rows back into the messages a human actually sent.
 *
 * Grouping is on `fanout_id`, which the SERVER stamps once per broadcast. It used to be inferred
 * from (channel, sender, sent_at, message) — and `sent_at` is stamped per row by the database, so
 * two rows of one broadcast differed by microseconds and never grouped. Every broadcast rendered
 * once per recipient, each copy claiming a single recipient.
 *
 * Inferring from the body instead would merge two genuinely separate sends of identical text, which
 * is why the group has to be stated by the writer rather than reconstructed by the reader.
 *
 * Input order is preserved, and rows are assumed oldest-first.
 */
export function collapse(messages: ZoneMessage[]): HistoryEntry[] {
  const byFanout = new Map<string, HistoryEntry>()
  const order: string[] = []

  for (const row of messages) {
    // A row with no fanout_id predates the column; it stands alone rather than being merged into
    // an unrelated neighbour.
    const key = row.fanout_id ?? `row:${row.id}`
    const existing = byFanout.get(key)
    if (existing === undefined) {
      byFanout.set(key, {
        key,
        seq: row.seq,
        source_type: row.source_type,
        source_name: row.source_name,
        sender: row.sender,
        message: row.message,
        recipients: [row.recipient],
        states: [row.state],
        sent_at: row.sent_at,
        first_read_at: row.first_read_at,
      })
      order.push(key)
    } else {
      existing.recipients.push(row.recipient)
      existing.states.push(row.state)
      existing.first_read_at = earliest(existing.first_read_at, row.first_read_at)
      // Pages are fetched newest-first, so a later-arriving page can carry a LOWER seq for the same
      // fan-out. The lowest is the anchor.
      existing.seq = Math.min(existing.seq, row.seq)
    }
  }

  return order.flatMap((key) => {
    const entry = byFanout.get(key)
    return entry === undefined ? [] : [entry]
  })
}
